/**
 * Notification Toast Component
 *
 * Displays in-app toast notifications when new notifications arrive
 * - Auto-dismiss after 5 seconds
 * - Click to navigate to related entity
 * - Manual close button
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Bell, FileText, UserPlus, MessageSquare, AlertCircle } from 'lucide-react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';

export function NotificationToast() {
  const navigate = useNavigate();
  const { toastNotification, dismissToast } = useNotifications();
  const { role } = useSupabaseAuth();

  // Auto-dismiss is handled in NotificationContext (5 seconds)

  useEffect(() => {
    // Play notification sound (optional)
    if (toastNotification) {
      // You can add audio notification here if desired
      // const audio = new Audio('/notification-sound.mp3');
      // audio.play().catch(() => {});
    }
  }, [toastNotification]);

  if (!toastNotification) return null;

  const handleClick = () => {
    // Navigate to related entity based on notification type
    const { notification_type, related_rfq_id, related_application_id, related_request_id } = toastNotification;

    dismissToast();

    // Route based on notification type and user role
    if (notification_type === 'rfq_inclusion_requested' || notification_type === 'rfq_invited') {
      if (role === 'recruiter' || role === 'admin') {
        navigate(`/recruiter/rfqs`);
      }
    } else if (notification_type === 'new_application') {
      if (role === 'recruiter' || role === 'admin') {
        navigate(`/recruiter/applications`);
      }
    } else if (notification_type === 'candidate_request') {
      if (role === 'recruiter' || role === 'admin') {
        navigate(`/recruiter/requests`);
      }
    } else if (notification_type === 'file_deletion_request') {
      if ((role === 'recruiter' || role === 'admin') && toastNotification.related_message_id) {
        navigate(`/recruiter/candidates/${toastNotification.related_message_id}`);
      }
    } else if (notification_type === 'new_message') {
      // Future: navigate to messages
      navigate(`/${role}/notifications`);
    } else {
      // Default: go to notifications inbox
      navigate(`/${role}/notifications`);
    }
  };

  const getIcon = () => {
    const iconClass = "w-5 h-5";

    switch (toastNotification.notification_type) {
      case 'rfq_inclusion_requested':
      case 'rfq_invited':
        return <FileText className={iconClass} />;
      case 'new_application':
        return <UserPlus className={iconClass} />;
      case 'candidate_request':
        return <AlertCircle className={iconClass} />;
      case 'new_message':
        return <MessageSquare className={iconClass} />;
      default:
        return <Bell className={iconClass} />;
    }
  };

  const getColorClasses = () => {
    switch (toastNotification.notification_type) {
      case 'rfq_invited':
        return 'bg-green-50 border-green-200 text-green-900';
      case 'new_application':
        return 'bg-blue-50 border-blue-200 text-blue-900';
      case 'candidate_request':
        return 'bg-amber-50 border-amber-200 text-amber-900';
      default:
        return 'bg-white border-slate-200 text-slate-900';
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-5 duration-300">
      <div
        className={`
          ${getColorClasses()}
          border rounded-lg shadow-lg max-w-md cursor-pointer
          transition-all hover:shadow-xl
        `}
        onClick={handleClick}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0 mt-0.5">
              {getIcon()}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm mb-1">
                {toastNotification.title}
              </h4>
              <p className="text-sm opacity-90 line-clamp-2">
                {toastNotification.body}
              </p>
              <p className="text-xs opacity-70 mt-1">
                Click to view details
              </p>
            </div>

            {/* Close button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                dismissToast();
              }}
              className="flex-shrink-0 p-1 hover:bg-black/5 rounded transition-colors"
              aria-label="Dismiss notification"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Progress bar for auto-dismiss */}
        <div className="h-1 bg-black/10 rounded-b-lg overflow-hidden">
          <div
            className="h-full bg-black/20 animate-toast-progress"
            style={{
              animation: 'toast-progress 5s linear forwards'
            }}
          />
        </div>
      </div>

      <style jsx>{`
        @keyframes toast-progress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
        .animate-toast-progress {
          animation: toast-progress 5s linear forwards;
        }
      `}</style>
    </div>
  );
}
